import { IDirective, IDirectiveFactory, IQService, IScope } from "angular";
import { IModalScope, IModalService } from "angular-ui-bootstrap";
import { AssigneeActions, IAssigneeIdMap, IAssigneesStoreState } from "@gtmhub/assignees";
import { CommentService, IComment } from "@gtmhub/comments";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { INgRedux } from "@gtmhub/state-management";
import { ITeamsIdMap, ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { getAllTeamMembersIncludingManager } from "@gtmhub/teams/redux/teams-selectors";
import { getCurrentUserId } from "@gtmhub/users";
import { unique } from "@gtmhub/util";

export interface IViewListGoal {
  id: string;
  attainment: number;
  name: string;
  ownerids: string[];
}
export interface IAskForUpdateScope extends IModalScope {
  postComment(): void;
  close(): void;
  currentUserId: string;
  indicators: {
    posting: boolean;
  };
  newCommentText: string;
  assigneesIdMap: IAssigneeIdMap;
  commentMentionsChanged(mentions: string[]): void;
}
export interface IListOfObjectivesScope extends IModalScope {
  openCommentsModal(): void;
  title: string;
  goals: IViewListGoal[];
  assigneesMap: IAssigneeIdMap;
}
export interface IViewListScope extends IScope, IScopeFromRedux {
  viewList(): void;
  objectives: IViewListGoal[];
  title: string;
  currentUserId: string;
}

interface IScopeFromRedux {
  teamsIdMap: ITeamsIdMap;
  assigneesIdMap: IAssigneeIdMap;
}

const bindStateToScope = (state: IAssigneesStoreState & ITeamsStoreState): IScopeFromRedux => ({
  assigneesIdMap: state.assignees.map,
  teamsIdMap: state.teams.map,
});

export class Viewobjectivelist implements IDirective {
  public scope = {
    objectives: "=",
    title: "@",
  };
  public restrict = "E";
  public transclude = true;
  public template = `<a class="link-accent" ng-click="viewList()" gh-track="Ask For Update" gh-track-meta="{screen_name: 'view_list'}">View list</a>`;

  constructor(
    private $uibModal: IModalService,
    private commentService: CommentService,
    private $q: IQService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $ngRedux: INgRedux,
    private assigneeActions: AssigneeActions,
    private teamsActions: TeamsActions
  ) {}

  private $scope: IViewListScope;
  private closeListOfObjectivesModal: () => boolean;

  public link(scope: IViewListScope): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$scope = scope;
    this.$scope.viewList = function () {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const directiveScope = this;
      ctrl.$uibModal.open({
        templateUrl: require("../views/list-of-objectives.jade"),
        windowClass: "medium list-of-objectives",
        controller: [
          "$scope",
          function ($scope: IListOfObjectivesScope) {
            ctrl.closeListOfObjectivesModal = $scope.$close;
            $scope.goals = directiveScope.objectives;
            $scope.title = directiveScope.title;
            $scope.assigneesMap = directiveScope.assigneesIdMap;
            $scope.openCommentsModal = () => ctrl.openCommentsModal(directiveScope.objectives);
          },
        ],
      });
    };
    this.$scope.currentUserId = getCurrentUserId();

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());
  }

  private openCommentsModal = (objectives: IViewListGoal[]): void => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../views/ask-for-update.jade"),
      windowClass: "medium ask-for-update",
      controller: [
        "$scope",
        function ($scope: IAskForUpdateScope) {
          $scope.assigneesIdMap = ctrl.$scope.assigneesIdMap;
          $scope.currentUserId = ctrl.$scope.currentUserId;
          $scope.indicators = { posting: false };
          $scope.newCommentText = "Please update";
          let mentions: string[] = [];

          $scope.commentMentionsChanged = (changedMentions: string[]): void => {
            mentions = changedMentions;
          };

          $scope.postComment = () => {
            $scope.indicators.posting = true;

            const commentsPromises = objectives.map((goal) => {
              const goalOwnersMentionArray = ctrl.mentionAllGoalOwners(goal);

              const newComment: IComment = {
                mentioned: unique([...mentions, ...goalOwnersMentionArray]),
                text: $scope.newCommentText,
                targetType: "goal",
                targetId: goal.id,
                targetTitle: goal.name,
              };

              return ctrl.commentService.post(newComment);
            });

            ctrl.$q
              .all(commentsPromises)
              .then(
                () => {
                  $scope.indicators.posting = false;
                  $scope.$close();
                  ctrl.closeListOfObjectivesModal();
                },
                (error) => {
                  ctrl.uiErrorHandlingService.handleIndicator(error, { indicator: "posting" });
                }
              )
              .catch((error) => {
                ctrl.uiErrorHandlingService.handleModal(error);
              });
          };
        },
      ],
    });
  };

  private mentionAllGoalOwners = (goal: IViewListGoal): string[] => {
    const allMentions = [];
    if (goal.ownerids) {
      goal.ownerids.forEach((ownerId) => {
        const ownerType = this.$scope.assigneesIdMap[ownerId].type;

        if (ownerType === "team") {
          const teamMembers = getAllTeamMembersIncludingManager(this.$scope.teamsIdMap[ownerId]);
          allMentions.push(...teamMembers);
        } else {
          allMentions.push(ownerId);
        }
      });
    }
    return allMentions;
  };

  public static factory(): IDirectiveFactory {
    const directive = (
      $uibModal: IModalService,
      commentService: CommentService,
      $q: IQService,
      uiErrorHandlingService: UIErrorHandlingService,
      $ngRedux: INgRedux,
      assigneeActions: AssigneeActions,
      teamsActions: TeamsActions
    ) => new Viewobjectivelist($uibModal, commentService, $q, uiErrorHandlingService, $ngRedux, assigneeActions, teamsActions);
    directive.$inject = ["$uibModal", "CommentService", "$q", "UIErrorHandlingService", "$ngRedux", "AssigneeActions", "TeamsActions"];

    return directive;
  }
}
